<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchUser, saveUser } from "./api";
import { toDateInput } from "@/lib/date";

const route = useRoute();
const router = useRouter();
const id = route.params.id;
const loading = ref(true), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), notFound = ref(false);
const form = ref({ name: "", display_name: "", company: "", department: "", job_title: "", job_classification: "", dob: "", hiring_date: "", proxy_number: "" });
let original = null;

onMounted(async () => {
  try {
    const u = await fetchUser(id);
    if (!u) { notFound.value = true; return; }
    original = u;
    // Backend returns the raw Firestore doc: dates are { _seconds, _nanoseconds }
    // timestamps under `dob` and `hiringDate` (camelCase) — not `hiring_date`.
    form.value = { name: u.name ?? "", display_name: u.display_name ?? "", company: u.company ?? "", department: u.department ?? "", job_title: u.job_title ?? "", job_classification: u.job_classification ? `${u.job_classification.job_code}-${u.job_classification.job_description}` : "", dob: toDateInput(u.dob), hiring_date: toDateInput(u.hiringDate), proxy_number: u.proxy_number ?? "" };
  } catch { error.value = "Couldn't load user."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  if (!form.value.name.trim()) { validationError.value = "Name is required."; return; }
  const f = form.value;
  const payload = { ...(original || {}), name: f.name, display_name: f.display_name, company: f.company, department: f.department, job_title: f.job_title, job_classification: { job_code: f.job_classification?.split("-")[0] || "", job_description: f.job_classification?.split("-")[1] || "" }, dob: f.dob, hiring_date: f.hiring_date, proxy_number: f.proxy_number };
  saving.value = true;
  try { await saveUser(payload); success.value = true; setTimeout(() => router.push({ name: "users" }), 800); }
  catch { error.value = "Couldn't save user. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'users' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Edit User</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">User not found.</VAlert>
    <VCard v-else class="pa-4" max-width="800">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12" md="6"><VTextField v-model="form.name" label="Official Name" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.display_name" label="Preferred Name" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.company" label="Company" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.department" label="Department" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.job_title" label="Job Title" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.job_classification" label="Job Classification" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.dob" label="Date of Birth" type="date" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.hiring_date" label="Hiring Date" type="date" /></VCol>
          <VCol cols="12"><VTextField v-model="form.proxy_number" label="Proxy Number" /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mt-4 mb-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">Save Changes</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'users' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
